import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ArrowLeft,
  ShoppingCart,
  User,
  CreditCard,

  AlertTriangle,
} from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { useAuthStore } from "@/stores/authStore";
import { AnonymousClientForm } from "@/components/business/pos/AnonymousClientForm";
import { useToast } from "@/hooks/useToast";
import { useInitiatePayment } from "@/hooks/useInitiatePayment";
import { apiClient } from "@/services";

type CheckoutStep = "client-data" | "review" | "payment";

export const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { items, getTotalPrice, isAnonymous, convertAnonymousToAuthenticated } =
    useCartStore();
  const { user } = useAuthStore();
  const { toast } = useToast();
  const { mutateAsync: initiatePayment, isPending: isPaymentPending } =
    useInitiatePayment();

  const [currentStep, setCurrentStep] = useState<CheckoutStep>("client-data");
  const [isProcessing, setIsProcessing] = useState(false);
  const shouldBypassTransbank = Boolean(
    user?.roleIds?.some((id) => id === 10 || id === 11)
  );

  // Si no hay items en el carrito, redirigir a la tienda
  useEffect(() => {
    if (items.length === 0) {
      navigate("/");
      toast({
        title: "Carrito vacío",
        description: "Agrega productos antes de continuar",
        variant: "destructive",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]); // navigate y toast son estables, no necesitan estar en deps

  // Si el usuario está autenticado, ir directamente al review
  useEffect(() => {
    if (user && currentStep === "client-data") {
      setCurrentStep("review");
    }
  }, [user, currentStep]);

  const handleClientDataComplete = () => {
    setCurrentStep("review");
  };

  const handleClientDataCancel = () => {
    navigate("/");
  };

  const handleContinueToPayment = () => {
    setCurrentStep("payment");
  };

  // const handlePaymentComplete = () => {
  //   // setCurrentStep("qr-generation"); // This step is removed
  // };

  const handleTransbankPayment = async () => {
    try {
      await initiatePayment();
    } catch (error) {
      console.error("Error iniciando pago con Transbank:", error);
      toast({
        title: "Error",
        description: "No se pudo iniciar el pago. Intenta nuevamente.",
        variant: "destructive",
      });
    }
  };

  const handleConvertToAuthenticated = async () => {
    try {
      setIsProcessing(true);
      await convertAnonymousToAuthenticated();
      toast({
        title: "Cuenta creada",
        description: "Tu carrito ha sido transferido a tu nueva cuenta",
      });
      setCurrentStep("review");
    } catch (error) {
      console.error("Error convirtiendo a usuario autenticado:", error);
      toast({
        title: "Error",
        description: "No se pudo crear la cuenta. Continúa como invitado.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInternalApprovalPayment = async () => {
    try {
      setIsProcessing(true);
      const resp = await apiClient.post("/api/v1/payments/approve-internal", {});
      const data = resp.data?.data;
      if (resp.data?.success && data?.sessionToken) {
        window.location.href = `/payment/success?token=${data.sessionToken}&source=internal`;
        return;
      }

      // Fallback sin token de sesión
      if (data?.orderId && data?.amount && data?.authorizationCode) {
        window.location.href = `/payment/success?orderId=${data.orderId}&amount=${data.amount}&authCode=${encodeURIComponent(
          data.authorizationCode
        )}&source=internal&items=${encodeURIComponent(JSON.stringify(items))}`;
        return;
      }

      throw new Error("Respuesta inválida del servidor en aprobación interna");
    } catch (error) {
      console.error("Error en aprobación interna de pago:", error);
      toast({
        title: "Error",
        description: "No se pudo completar el pago interno.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const renderClientDataStep = () => (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate("/")} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver a la tienda
        </Button>

        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary/10 rounded-lg">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Datos de Contacto</h1>
            <p className="text-muted-foreground">
              Necesitamos tu información para procesar tu pedido
            </p>
          </div>
        </div>
      </div>

      <AnonymousClientForm
        onComplete={handleClientDataComplete}
        onCancel={handleClientDataCancel}
      />
    </div>
  );

  const renderReviewStep = () => (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate("/")} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver a la tienda
        </Button>

        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary/10 rounded-lg">
            <ShoppingCart className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Revisar Pedido</h1>
            <p className="text-muted-foreground">
              Confirma los productos y datos antes de continuar
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Resumen del pedido */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" />
                Productos ({items.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-4 p-4 border rounded-lg"
                >
                  <div className="flex-shrink-0">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-md"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center">
                        <ShoppingCart className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <h3 className="font-medium">{item.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      Cantidad: {item.quantity}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="font-medium">
                      ${(item.price * item.quantity).toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      ${item.price.toLocaleString()} c/u
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Resumen de pago */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Resumen de Pago</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>${getTotalPrice().toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Envío:</span>
                <span className="text-green-600">Gratis</span>
              </div>
              <Separator />
              <div className="flex justify-between font-bold text-lg">
                <span>Total:</span>
                <span>${getTotalPrice().toLocaleString()}</span>
              </div>

              {isAnonymous && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Comprando como invitado. Los datos se usarán solo para este
                    pedido.
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Button onClick={handleContinueToPayment} className="w-full">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pagar con Tarjeta
                </Button>

                {isAnonymous && (
                  <Button
                    variant="outline"
                    onClick={handleConvertToAuthenticated}
                    disabled={isProcessing}
                    className="w-full"
                  >
                    <User className="h-4 w-4 mr-2" />
                    {isProcessing ? "Creando cuenta..." : "Crear cuenta"}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );

  const renderPaymentStep = () => (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => setCurrentStep("review")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver al resumen
        </Button>

        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary/10 rounded-lg">
            <CreditCard className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Pago Online</h1>
            <p className="text-muted-foreground">
              Completa tu compra de forma segura con tarjeta
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Método de Pago</CardTitle>
          <CardDescription>Paga de forma segura con Transbank</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            {shouldBypassTransbank ? (
              <Button
                variant="default"
                className="h-16 justify-start w-full"
                onClick={handleInternalApprovalPayment}
                disabled={isProcessing}
              >
                <div className="flex items-center gap-3">
                  <CreditCard className="h-6 w-6 text-primary" />
                  <div className="text-left">
                    <div className="font-medium">Completar Pedido (Interno)</div>
                    <div className="text-sm text-muted-foreground">
                      Flujo sin Transbank para roles autorizados
                    </div>
                  </div>
                </div>
              </Button>
            ) : (
              <Button
                variant="outline"
                className="h-16 justify-start w-full"
                onClick={handleTransbankPayment}
                disabled={isPaymentPending}
              >
                <div className="flex items-center gap-3">
                  <CreditCard className="h-6 w-6 text-primary" />
                  <div className="text-left">
                    <div className="font-medium">Tarjeta de Crédito/Débito</div>
                    <div className="text-sm text-muted-foreground">
                      Paga de forma segura con Transbank
                    </div>
                  </div>
                </div>
              </Button>
            )}

            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                💡 <strong>Retira tu pedido con QR</strong>
                <br />
                Después del pago, recibirás un código QR para retirar tu pedido
                en el local
              </p>
            </div>

            {!shouldBypassTransbank ? (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Instrucciones importantes:</strong>
                  <br />
                  1. Se abrirá una ventana emergente con Transbank
                  <br />
                  2. Completa el pago con tus datos de tarjeta
                  <br />
                  3. <strong>No cierres la ventana</strong> hasta ver la confirmación
                  <br />
                  4. El sistema verificará automáticamente el estado del pago
                </AlertDescription>
              </Alert>
            ) : null}

            {!shouldBypassTransbank && isPaymentPending && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Iniciando pago con Transbank. Se abrirá una ventana emergente.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Renderizar el paso actual
  switch (currentStep) {
    case "client-data":
      return renderClientDataStep();
    case "review":
      return renderReviewStep();
    case "payment":
      return renderPaymentStep();
    default:
      return renderClientDataStep();
  }
};
